<template>
    <v-container v-if="!isForbidden">
        <v-tabs v-model="tabs" fixed-tabs>
            <v-tab>
                Read
            </v-tab>
            <v-tab :disabled="isAchiveDoc">
                Comments
            </v-tab>
            <v-tab v-show="useSharing" :disabled="isAchiveDoc">
                Shares
            </v-tab>
            <v-tab>
                Versions
            </v-tab>
            <slot name="extraTabs"></slot>
        </v-tabs>
        <v-tabs-items v-model="tabs">
            <v-tab-item>
                <v-container fluid v-if="showDocument">
                    <v-row>
                        <v-col cols="12" md="8" order-md="2">
                            <div class="d-flex justify-center justify-md-end">
                                <action-buttons
                                    state="Read"
                                    :permissions="userPermissions"
                                    :disabled="disableButtons"
                                    :entity-status="doc.statusId"
                                    :allow-copy="allowCopy"
                                    :can-print="canPrint"
                                    @edit="handleEdit"
                                    @submit="handleSubmit"
                                    @archive="handleArchive"
                                    @unarchive="handleUnarchive"
                                    @approve="handleApprove"
                                    @flag="handleFlag"
                                    @copy="handleCopy"
                                    @print="handlePrint"
                                />
                                <v-btn
                                    v-if="toursEnabled"
                                    icon
                                    small
                                    @click="replayReaderTour"
                                    title="Replay tour"
                                    aria-label="Replay tour"
                                    class="ml-1"
                                >
                                    <v-icon small>mdi-help-circle-outline</v-icon>
                                </v-btn>
                            </div>
                        </v-col>
                        <v-col cols="12" md="4" order-md="1">
                            <last-edit-details :entity="doc" />
                        </v-col>
                    </v-row>
                    <slot name="reader" v-bind:doc="doc"></slot>
                </v-container>
            </v-tab-item>
            <v-tab-item>
                <comment-control
                    :comments="comments"
                    :is-saving="isSaving"
                    :subscription="subscription"
                    @save="saveComment"
                    @edit="saveEdit"
                    @delete="deleteComment"
                    @subscription="saveSubscription"
                ></comment-control>
            </v-tab-item>
            <v-tab-item>
                <share-manager
                    :share-all="shareData.shareAll"
                    :shares="shareData.shareTeams"
                    :is-saving="isSaving"
                    :readonly="!canEditShares"
                    @save="saveShares"
                ></share-manager>
            </v-tab-item>
            <v-tab-item>
                <v-data-table
                    :headers="versionHeadings"
                    :items="versions"
                    :items-per-page="15"
                    dense
                    single-expand
                    show-expand
                    item-key="version"
                    class="elevation-1"
                    :loading="isLoading"
                    :footer-props="{
                        showFirstLastPage: true,
                        itemsPerPageOptions: [-1, 10, 15, 20, 25],
                        firstIcon: 'mdi-arrow-collapse-left',
                        lastIcon: 'mdi-arrow-collapse-right',
                        prevIcon: 'mdi-minus',
                        nextIcon: 'mdi-plus',
                    }"
                    @item-expanded="getVersion"
                >
                    <template v-slot:[`expanded-item`]="{ headers }">
                        <td v-if="isLoadingVersion" :colspan="headers.length">
                            <v-progress-circular
                                :size="70"
                                :width="7"
                                color="purple"
                                indeterminate
                            ></v-progress-circular>
                        </td>
                        <td v-else :colspan="headers.length">
                            <slot name="versionReader" v-bind:doc="versionDoc">
                                <slot name="reader" v-bind:doc="versionDoc"></slot>
                            </slot>
                        </td>
                    </template>
                    <template v-slot:[`item.createdDate`]="{ item }">
                        {{ item.createdDate | luxonSortableDateTime }}
                    </template>
                    <template v-slot:[`item.createdBy`]="{ item }">
                        <user-display-name :user="item.createdBy"></user-display-name>
                    </template>
                </v-data-table>
            </v-tab-item>
            <slot name="extraTabItems" v-bind:userPermissions="userPermissions"></slot>
        </v-tabs-items>
    </v-container>
    <forbidden v-else :name="entity"></forbidden>
</template>
<script>
import api from '@/api'
import { mapState } from 'vuex'
import CommentControl from './CommentControl'
import ShareManagerControl from './ShareManagerControl'
import entities from './entities'
import editingStatuses from './editingStatuses'
import permissionsSet from './permissions'
import { returnToOrigin } from '@/utils/navigationOrigin'
import { downloadFile } from '@/utils/downloadFile'
import { startReaderTour, resetReaderTour, TOURS_ENABLED } from '@/tour/index'
export default {
    name: 'EntityReader',
    components: {
        'comment-control': CommentControl,
        'share-manager': ShareManagerControl,
    },
    props: {
        entity: {
            type: String,
            required: true,
            validator: value => {
                return entities.includes(value)
            },
        },
        apiName: {
            type: String,
            required: false,
        },
        id: {
            type: [Number, String],
            required: true,
            validator: value => {
                return !!value
            },
        },
        useSharing: {
            type: Boolean,
            required: true,
        },
        editRouteName: {
            type: String,
            required: false,
        },
        homeRouteName: {
            type: String,
            required: false,
        },
        allowCopy: {
            type: Boolean,
            required: false,
            default: false,
        },
        copyRouteName: {
            type: String,
            required: false,
        },
        canPrint: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isForbidden: false,
            doc: undefined,
            isLoading: false,
            isSaving: false,
            tabs: null,
            comments: [],
            subscription: { isSubscribed: false, isMuted: false, canMute: false },
            shareData: {
                shareAll: false,
                shareTeams: [],
            },
            versions: [],
            versionHeadings: [
                { text: 'Version', value: 'version' },
                { text: 'Status', value: 'statusText' },
                { text: 'Create Date', value: 'createdDate' },
                { text: 'Created By', value: 'createdBy' },
                { text: '', value: 'data-table-expand' },
            ],
            isLoadingVersion: false,
            versionDoc: {},
        }
    },
    computed: {
        ...mapState(['user']),
        toursEnabled() {
            return TOURS_ENABLED
        },
        userPermissions() {
            if (this.user && this.user[this.entity] && this.doc) {
                let permissions = []
                if (this.user.teams.includes(this.doc.teamId)) permissions.push(...this.user[this.entity].owned)
                else if (this.doc.isMonsterManual) {
                    // Monster Manual grants visibility WITHOUT a share, so the shared tier cannot
                    // be trusted here — its Edit/Archive would 403 on click for a user whose
                    // authority comes from an actual share. The All tier has no such dependency:
                    // EditAll/ApproveAll/DeleteAll (and admins, who satisfy all of them) are
                    // exactly the backend's share-independent branches in EntityService.CanEdit
                    // and HasStatusUpdatePermission. Grant those; a shared-tier-only user has an
                    // empty All tier and stays read + comment + copy-as-own, as intended.
                    permissions.push(...this.user[this.entity].all)
                    if (this.user[this.entity].owned.includes(permissionsSet.Create))
                        permissions.push(permissionsSet.CanCopyAsOwn)
                } else {
                    permissions.push(...this.user[this.entity].shared)
                    if (this.user[this.entity].owned.includes(permissionsSet.Create))
                        permissions.push(permissionsSet.CanCopyAsOwn)
                }
                return permissions
            } else {
                return []
            }
        },
        apiEntityAddress() {
            return this.apiName || `${this.entity}s`
        },
        showDocument() {
            return this.doc
        },
        disableButtons() {
            return this.isLoading || this.isSaving
        },
        canEditShares() {
            if (this.user && this.user[this.entity] && this.doc) {
                const p = this.user[this.entity]
                const onTeam = this.user.teams.includes(this.doc.teamId)
                return (
                    p.all.includes(permissionsSet.Edit) ||
                    p.all.includes(permissionsSet.Share) ||
                    (onTeam &&
                        (this.userPermissions.includes(permissionsSet.Edit) ||
                            this.userPermissions.includes(permissionsSet.Share)))
                )
            } else {
                return false
            }
        },
        isAchiveDoc() {
            return this.doc ? this.doc.statusId === -1 : false
        },
        capsEnt() {
            return this.entity[0].toUpperCase() + this.entity.substring(1)
        },
        editRoute() {
            return this.editRouteName ? this.editRouteName : `edit${this.capsEnt}`
        },
        copyRoute() {
            return this.copyRouteName ? this.copyRouteName : `copy${this.capsEnt}`
        },
        homeRoute() {
            return this.homeRouteName ? this.homeRouteName : this.entity
        },
        // The tab the current URL asks for. Notification links carry ?tab=comments;
        // in-app links use the :tabName? route param.
        routeTab() {
            return this.tabIndexFromName(this.$route.params.tabName || this.$route.query.tab)
        },
    },
    watch: {
        id: function() {
            // vue-router reuses this component when only the id changes (e.g. following a
            // second notification while reading the first), so mounted() does not re-run and
            // the tab index does not change. Everything the previous entity loaded has to be
            // cleared and reloaded here, or the Comments/Shares/Versions tabs keep showing it.
            this.resetEntityState()
            const tab = this.routeTab
            if (this.tabs === tab) this.loadTab(tab)
            // A changed tab index is picked up by the tabs watcher below.
            else this.tabs = tab
            // loadTab only fetches the document for the Read tab; the header and action
            // buttons need it on every tab.
            if (tab !== 0) this.getEntity()
        },
        // Same entity, different tab in the URL — e.g. following a notification for the
        // entity already open on its Read tab.
        routeTab: function(val) {
            this.tabs = val
        },
        tabs: function(val) {
            this.loadTab(val)
        },
        doc(val) {
            if (val) {
                // startReaderTour returns early if already seen (guarded by localStorage)
                this.$nextTick(() => startReaderTour())
            }
        },
    },
    mounted() {
        this.tabs = this.routeTab
    },
    methods: {
        loadTab(val) {
            if (val === 0) this.getEntity()
            if (val === 1) this.loadComments()
            if (val === 2) this.loadShares()
            if (val === 3) this.loadVersions()
        },
        // Wipe the previously viewed entity so no tab can render its data while the
        // replacement is in flight.
        resetEntityState() {
            this.isForbidden = false
            this.doc = undefined
            this.comments = []
            this.subscription = { isSubscribed: false, isMuted: false, canMute: false }
            this.shareData = { shareAll: false, shareTeams: [] }
            this.versions = []
            this.versionDoc = {}
        },
        tabIndexFromName(name) {
            switch (String(name || '').toLowerCase()) {
                case 'comments': return 1
                case 'shares': return 2
                case 'versions': return 3
                default: return 0
            }
        },
        replayReaderTour() {
            resetReaderTour()
            this.$nextTick(() => startReaderTour())
        },
        getEntity() {
            //Comparison on String value as that's what is passed from router
            if (this.doc && parseInt(this.doc.keyId) === this.id) return
            this.isLoading = true
            api.get(`/${this.apiEntityAddress}/${this.id}`)
                .then(res => {
                    this.doc = res
                })
                .catch(e => {
                    if (e.response.status === 403) {
                        //this.$router.push({ name: 'forbidden' })
                        this.isForbidden = true
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        handleEdit() {
            this.$router.push({ name: this.editRoute, params: { id: this.id } })
        },
        handleCopy() {
            this.$router.push({ name: this.copyRoute, params: { id: this.id } })
        },
        handlePrint() {
            // isSaving drives disableButtons, so the whole action bar disables while the PDF
            // is generated — a single encounter is fast, but the pack is not, and the same
            // handler serves every entity that opts in.
            this.isSaving = true
            // silent: true — this handler shows its own specific error message below; without
            // it the global interceptor would show a second, competing snackbar, and would
            // render the blob error body as "[object Blob]".
            downloadFile(`/${this.apiEntityAddress}/print/single/${this.id}`, { silent: true })
                .then(delivered => {
                    if (!delivered) this.showSnackbar('info', `There was nothing to print for this ${this.entity}.`)
                })
                .catch(() => {
                    this.showSnackbar('error', `Could not print this ${this.entity}.`)
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        goBack() {
            // Return to the list/timetable the user came from (captured on navigation) with
            // its filter context intact via keep-alive, rather than a history-stack-dependent
            // go(-1) that can land on an intermediate edit/new screen. Falls back to the entity
            // home route when there is no recorded origin (e.g. a deep link).
            returnToOrigin(this.$router, this.homeRoute)
        },
        handleSubmit() {
            this.isSaving = true
            var model = { version: this.doc.version, status: editingStatuses.Submitted }
            api.patch(`/${this.apiEntityAddress}/${this.doc.keyId}/status`, model)
                .then(() => {
                    //this.doc = res
                    this.showSnackbar('info', `${this.entity} Submitted`)
                    this.goBack()
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        handleArchive() {
            this.isSaving = true
            var model = { version: this.doc.version, status: editingStatuses.Archived }
            api.patch(`/${this.apiEntityAddress}/${this.doc.keyId}/status`, model)
                .then(() => {
                    this.showSnackbar('info', `${this.entity} Archived`)
                    this.goBack()
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        handleUnarchive() {
            this.isSaving = true
            var model = { version: this.doc.version, status: editingStatuses.Editing }
            api.patch(`/${this.apiEntityAddress}/${this.doc.keyId}/status`, model)
                .then(res => {
                    this.doc = res
                    this.showSnackbar('info', `${this.entity} Restored`)
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        handleApprove() {
            if (this.$listeners.approve) {
                this.$emit('approve', this.doc)
            } else {
                this.isSaving = true
                var model = { version: this.doc.version, status: editingStatuses.Approved }
                api.patch(`/${this.apiEntityAddress}/${this.doc.keyId}/status`, model)
                    .then(() => {
                        this.showSnackbar('info', `${this.entity} Approved`)
                        this.goBack()
                    })
                    .finally(() => {
                        this.isSaving = false
                    })
            }
        },
        handleFlag() {
            this.isSaving = true
            var model = { version: this.doc.version, status: editingStatuses.Flagged }
            api.patch(`/${this.apiEntityAddress}/${this.doc.keyId}/status`, model)
                .then(() => {
                    this.showSnackbar('info', `${this.entity} Flagged`)
                    this.goBack()
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        loadComments() {
            this.isLoading = true
            api.get(`/${this.apiEntityAddress}/${this.id}/comments`)
                .then(res => {
                    if (Array.isArray(res)) {
                        this.comments = res
                    } else {
                        this.comments = []
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
            this.loadSubscription()
        },
        loadSubscription() {
            api.get(`/${this.apiEntityAddress}/${this.id}/subscription`)
                .then(data => (this.subscription = data))
                .catch(() => {})
        },
        saveSubscription(isMuted) {
            api.post(`/${this.apiEntityAddress}/${this.id}/subscription`, { isMuted })
                .then(data => (this.subscription = data))
                .catch(() => this.showSnackbar('error', 'Could not update your subscription.'))
        },
        saveComment(comment) {
            this.isSaving = true
            comment.keyId = this.id
            api.post(`/${this.apiEntityAddress}/${this.id}/comments`, comment)
                .then(res => {
                    // Commenting is what creates the subscription, so the unsubscribe control
                    // only becomes applicable once the POST succeeds. Nothing else reloads it
                    // here (comments are appended locally rather than re-fetched), so without
                    // this the control would not appear until the user left the Comments tab
                    // and came back. Reloaded first so the early return below — a reply whose
                    // parent is missing from the local list — cannot skip it.
                    this.loadSubscription()
                    if (res.parentId) {
                        let parent = this.comments.find(x => x.id === res.parentId)
                        if (!parent) return
                        if (!parent.childComments) parent.childComments = []
                        parent.childComments.push(res)
                    } else {
                        this.comments.push(res)
                    }
                })
                .catch(err => {
                    this.showSnackbar('error', err)
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        saveEdit(comment) {
            this.isSaving = true
            comment.keyId = this.id
            api.put(`/${this.apiEntityAddress}/${this.id}/comments`, comment)
                .then(res => {
                    if (res.editParentId) {
                        let parent = this.findCommentById(res.editParentId)
                        if (!parent) return
                        parent.edits.push(res)
                    }
                })
                .catch(err => {
                    this.showSnackbar('error', err)
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        deleteComment(commentId) {
            this.isSaving = true
            api.delete(`/${this.apiEntityAddress}/${this.id}/comments/${commentId}`)
                .then(() => {
                    this.removeCommentById(commentId)
                    this.showSnackbar('success', 'Comment Deleted')
                })
                .catch(err => {
                    this.showSnackbar('error', err)
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        findCommentById(id) {
            let foundComment = this.comments.find(x => x.id === id)
            if (foundComment) return foundComment
            this.comments.some(x => {
                foundComment = x.childComments.find(x => x.id === id)
                return !!foundComment
            })
            return foundComment
        },
        removeCommentById(commentId) {
            let comment = this.findCommentById(commentId)
            if (comment) {
                if (comment.parentId) {
                    let parent = this.findCommentById(comment.parentId)
                    let childCommentIndex = parent.childComments.findIndex(x => x.id === commentId)
                    if (childCommentIndex > -1) {
                        parent.childComments.splice(childCommentIndex, 1)
                    }
                }
                let commentIndex = this.comments.findIndex(x => x.id === commentId)
                if (commentIndex > -1) {
                    this.comments.splice(commentIndex, 1)
                }
            }
        },
        loadShares() {
            this.isLoading = true
            api.get(`/${this.apiEntityAddress}/${this.id}/shares`)
                .then(res => {
                    this.shareData = res
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        saveShares(shareAll, teams) {
            this.isSaving = true
            api.post(`/${this.apiEntityAddress}/${this.id}/shares`, { shareAll, teams })
                .then(res => {
                    this.showSnackbar('info', 'Shares Updated')
                    this.shareData = res
                })
                .catch(err => {
                    this.showSnackbar('error', err)
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        loadVersions(refresh) {
            if (!!refresh && this.versions && this.versions.length > 0) return
            this.isLoading = true
            api.get(`/${this.apiEntityAddress}/${this.id}/versions`)
                .then(res => {
                    this.versions = res
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        getVersion(e) {
            if (!e.value) return
            this.isLoadingVersion
            api.get(`/${this.apiEntityAddress}/${this.id}/versions/${e.item.version}`)
                .then(res => {
                    this.versionDoc = res
                })
                .finally(() => {
                    this.isLoadingVersion = false
                })
        },
    },
}
</script>
